import { useEffect, useState } from 'preact/hooks';
import { savePreview, slugify } from '../../lib/download';
import { polygonMask } from '../../lib/mask';
import type { Tenant } from '../../lib/tenant';
import { autoQuad } from '../../render/autoQuad';
import type { Vec2 } from '../../render/homography';
import { BeforeAfter } from '../components/BeforeAfter';
import { ColorGrid } from '../components/ColorGrid';
import { FloorStage } from '../components/FloorStage';
import { MaskOverlay, MaskToolbar, useMaskEditor } from '../components/MaskEditor';
import { OutlineOverlay, OutlineToolbar } from '../components/OutlineEditor';
import { PerspectiveHandles } from '../components/PerspectiveHandles';
import { ScanningOverlay } from '../components/ScanningOverlay';
import { SystemTabs } from '../components/SystemTabs';
import { selection, startScale, type Session } from '../session';
import { useFloorRenderer } from '../useFloorRenderer';

interface Props {
  tenant: Tenant;
  session: Session;
  update(patch: Partial<Session>): void;
  /** Stem wall switched; detects the walls first when needed. */
  onStemWall(on: boolean): void;
  onStartOver(): void;
}

type Mode = 'view' | 'edges' | 'adjust' | 'outline';

export function Editor({ tenant, session, update, onStemWall, onStartOver }: Props) {
  const cfg = tenant.config;
  const [mode, setMode] = useState<Mode>('view');
  const [holding, setHolding] = useState(false);
  const [saving, setSaving] = useState(false);
  const [outline, setOutline] = useState<Vec2[]>([]);
  const [edgeHint, setEdgeHint] = useState<string | null>(null);
  const edges = useMaskEditor();
  const { canvasRef, unsupported, stemAvailable, watermark } = useFloorRenderer(tenant, session, mode === 'edges' || mode === 'outline');
  const { system, color, spaceType } = selection(cfg, session);
  // The slider shows size relative to the system's start (1x). A system with
  // its own start value never goes below it, only larger.
  const start = startScale(cfg, session.systemId);
  const { width: W, height: H } = session.photo;

  const openEdges = (hint: string | null = null) => {
    if (!session.mask) return;
    edges.open(session.mask, session.detectedMask ?? session.mask, W, H);
    setEdgeHint(hint);
    setMode('edges');
  };

  // Detection finished: open the right tool straight away when needed.
  useEffect(() => {
    if (session.status === 'failed') {
      setOutline([]);
      setMode('outline');
    } else if (session.status === 'ready' && session.lowConfidence) {
      openEdges('Paint over your floor to help us out');
    }
  }, [session.status]);

  if (unsupported) return <WebGLMessage tenant={tenant} onStartOver={onStartOver} />;

  const pickSystem = (id: string) => {
    const next = cfg.systems.find((s) => s.id === id)!;
    update({ systemId: id, colorId: next.colors[0].id, patternScale: startScale(cfg, id) });
  };

  const finishEdges = (mask: Uint8Array) => {
    update({ mask, quad: session.quadManual ? session.quad : autoQuad(mask, W, H), lowConfidence: false });
    edges.close();
    setMode('view');
  };

  const finishOutline = () => {
    const mask = polygonMask(outline, W, H);
    update({ mask, detectedMask: mask, quad: autoQuad(mask, W, H), quadManual: false, status: 'ready' });
    setMode('view');
  };

  const resetAdjust = () => {
    if (session.mask) update({ quad: autoQuad(session.mask, W, H), quadManual: false, patternScale: startScale(cfg, session.systemId) });
  };

  const download = async () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    setSaving(true);
    try {
      await savePreview(canvas, `${slugify(`${cfg.brand.name} ${color.name} ${system.name}`)}.jpg`);
    } catch (err) {
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const hold = (on: boolean) => () => setHolding(on);
  const ready = session.status === 'ready' && !!session.mask;
  const pill = 'min-h-11 shrink-0 rounded-full px-4 text-sm font-medium text-ink ring-1 ring-line hover:bg-paper disabled:opacity-40';

  return (
    <div class="min-h-(--fv-screen) pb-28 lg:pb-10">
      <header class="mx-auto flex max-w-6xl items-center justify-between px-4 py-3 lg:px-6">
        <img src={tenant.asset(cfg.brand.logo)} alt={cfg.brand.name} class="h-7 w-auto" />
        <button type="button" onClick={onStartOver} class="min-h-11 px-1 text-sm font-medium text-muted underline-offset-4 hover:text-ink hover:underline">
          New photo
        </button>
      </header>

      <div class="mx-auto max-w-6xl lg:grid lg:grid-cols-[minmax(0,1fr)_380px] lg:gap-8 lg:px-6">
        <section aria-label="Your floor preview">
          <FloorStage width={W} height={H} canvasRef={canvasRef} alt={`Preview: your ${spaceType.label.toLowerCase()} with ${color.name} ${system.name}`}>
            {session.status === 'detecting' && <ScanningOverlay />}
            {mode === 'view' && ready && <BeforeAfter before={session.photo} holding={holding} watermark={watermark} />}
            {mode === 'edges' && edges.state && <MaskOverlay state={edges.state} />}
            {mode === 'adjust' && <PerspectiveHandles quad={session.quad} onChange={(quad) => update({ quad, quadManual: true })} />}
            {mode === 'outline' && <OutlineOverlay points={outline} onAdd={(p) => setOutline([...outline, p])} />}
          </FloorStage>

          {mode === 'view' && (
            <>
              <div class="flex items-center gap-2 overflow-x-auto px-4 pt-3 sm:px-0">
                <button
                  type="button"
                  class="inline-flex min-h-11 shrink-0 items-center gap-2 rounded-full bg-brand px-4 text-sm font-semibold text-brand-text hover:brightness-110 disabled:opacity-40"
                  disabled={!ready || saving}
                  aria-busy={saving}
                  onClick={download}
                >
                  <svg aria-hidden="true" viewBox="0 0 20 20" class="h-4 w-4" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M10 3v10M5.5 8.5 10 13l4.5-4.5M4 16.5h12" />
                  </svg>
                  Download
                </button>
                <button type="button" class={pill} disabled={!ready} onClick={() => openEdges()}>
                  Fix edges
                </button>
                <button type="button" class={pill} disabled={!ready} onClick={() => setMode('adjust')}>
                  Adjust
                </button>
                {ready && cfg.stemWall && session.upload && (
                  <button
                    type="button"
                    class={`${pill} inline-flex items-center gap-2`}
                    aria-pressed={session.stemWall}
                    aria-busy={session.stemWall && session.wallStatus === 'loading'}
                    onClick={() => onStemWall(!session.stemWall)}
                  >
                    Stem wall
                    <span aria-hidden="true" class={`relative h-5 w-9 rounded-full transition ${session.stemWall ? 'bg-brand' : 'bg-line'}`}>
                      <span class={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-all ${session.stemWall ? 'left-4.5' : 'left-0.5'}`} />
                    </span>
                  </button>
                )}
                {ready && (
                  <button
                    type="button"
                    class={`${pill} ml-auto lg:hidden`}
                    onPointerDown={hold(true)}
                    onPointerUp={hold(false)}
                    onPointerLeave={hold(false)}
                    onPointerCancel={hold(false)}
                    onKeyDown={(e) => (e.key === ' ' || e.key === 'Enter') && setHolding(true)}
                    onKeyUp={hold(false)}
                    onContextMenu={(e) => e.preventDefault()}
                  >
                    Hold to see original
                  </button>
                )}
              </div>
              {ready && session.stemWall && cfg.stemWall && (
                <p class="px-4 pt-2 text-xs font-medium text-ink sm:px-0" role="status">
                  {session.wallStatus === 'loading'
                    ? 'Finding your walls...'
                    : session.wallStatus === 'failed'
                      ? 'We could not find the walls in this photo.'
                      : session.wallStatus === 'ready' && !stemAvailable
                        ? 'No wall base found in this photo.'
                        : null}
                </p>
              )}
              <p class="px-4 pt-2 text-xs text-muted sm:px-0">{cfg.copy.disclaimer}</p>
            </>
          )}

          {mode === 'edges' && edges.state && (
            <MaskToolbar
              state={edges.state}
              hint={edgeHint}
              onDone={finishEdges}
              onCancel={() => {
                edges.close();
                setMode('view');
              }}
            />
          )}

          {mode === 'adjust' && (
            <div class="space-y-3 px-4 pt-3 sm:px-0">
              <p class="text-sm text-muted">Line up the sides with the floor’s edges, such as where it meets the walls. The shape can be smaller than the floor.</p>
              <label class="block">
                <span class="flex justify-between text-sm font-semibold">
                  Pattern size <span class="font-normal text-muted">{(session.patternScale / start).toFixed(2)}x</span>
                </span>
                <input
                  type="range"
                  min={system.patternScale ?? 0.5}
                  max={Math.max(4, start * 2)}
                  step={0.05}
                  value={session.patternScale}
                  onInput={(e) => update({ patternScale: Number((e.currentTarget as HTMLInputElement).value) })}
                  class="mt-2 w-full accent-[var(--fv-primary)]"
                />
              </label>
              <div class="flex items-center gap-2">
                <button type="button" class={pill} onClick={resetAdjust}>
                  Reset
                </button>
                <span class="flex-1" />
                <button type="button" onClick={() => setMode('view')} class="min-h-11 rounded-full bg-ink px-5 text-sm font-semibold text-white">
                  Done
                </button>
              </div>
            </div>
          )}

          {mode === 'outline' && (
            <OutlineToolbar
              points={outline}
              onUndo={() => setOutline(outline.slice(0, -1))}
              onClear={() => setOutline([])}
              onDone={finishOutline}
            />
          )}
        </section>

        <aside class="mt-5 space-y-4 px-4 lg:mt-0 lg:px-0">
          <div>
            <p class="text-xs font-semibold uppercase tracking-wider text-muted">Your floor</p>
            <h2 class="mt-0.5 text-xl font-bold leading-tight">
              {color.name} <span class="font-medium text-muted">{system.name}</span>
            </h2>
          </div>
          <SystemTabs systems={cfg.systems} selected={system.id} onSelect={pickSystem} />
          <p class="text-sm text-muted">{system.description}</p>
          <ColorGrid system={system} selected={color.id} asset={tenant.asset} onSelect={(id) => update({ colorId: id })} />
        </aside>
      </div>
    </div>
  );
}

function WebGLMessage({ tenant, onStartOver }: { tenant: Tenant; onStartOver(): void }) {
  const { brand } = tenant.config;
  return (
    <main class="mx-auto flex min-h-(--fv-screen) max-w-md flex-col justify-center gap-4 px-6">
      <h1 class="text-2xl font-bold">Please update your browser</h1>
      <p class="text-muted">
        The floor preview needs a newer browser. Please update to the latest Safari, Chrome, Edge or Firefox and try again. You can also call {brand.name} at{' '}
        <a class="font-semibold text-ink underline" href={`tel:${brand.phone.replace(/[^\d+]/g, '')}`}>
          {brand.phone}
        </a>
        .
      </p>
      <button type="button" onClick={onStartOver} class="self-start text-sm font-medium underline">
        Back
      </button>
    </main>
  );
}
